import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { AgreementMetaDataConstants, MetadataHighlightsService, AgreementMetaDataUtils } from 'app/features/agreement';
import { Subject, takeUntil } from 'rxjs';

@Component({
    template: '',
    styleUrl: './base-highlight.component.scss'
})
export abstract class BaseHighlightComponent implements OnInit, OnDestroy {
    public readonly highlightsService = inject(MetadataHighlightsService);
    public readonly agreementMetaDataConstants = AgreementMetaDataConstants;
    public focusedFields = new Set<string>();

    private highlightedFields = new Set<string>();
    private destroy$ = new Subject<void>();

    public ngOnInit(): void {
        this.highlightsService.highlightedFields$.pipe(takeUntil(this.destroy$)).subscribe((fields) => {
            this.highlightedFields = fields;
        });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public onFieldFocus(fieldName: string | AgreementMetaDataConstants): void {
        const fieldNameStr =
            typeof fieldName === 'string' ? fieldName : AgreementMetaDataUtils.getEnumKeyByValue(fieldName);

        const enumValue =
            typeof fieldName === 'number' ? fieldName : AgreementMetaDataUtils.getEnumValueByKey(fieldNameStr);

        // Trigger PDF highlighting if field has a text reference
        if (enumValue !== undefined && this.highlightsService.hasEnumFieldTextReference(enumValue)) {
            // Notify service that this field was focused for PDF highlighting
            this.highlightsService.notifyFieldFocused(enumValue);
        }

        // Only process focus if the field was actually highlighted
        if (enumValue !== undefined) {
            const enumKeyName = AgreementMetaDataConstants[enumValue];
            if (this.highlightedFields.has(enumKeyName)) {
                this.focusedFields.add(fieldNameStr);
                this.highlightsService.removeHighlightedField(fieldNameStr);
            }
        }
    }

    public isFieldHighlighted(enumValue: AgreementMetaDataConstants): boolean {
        const fieldName = AgreementMetaDataConstants[enumValue];
        return this.highlightedFields.has(fieldName) && !this.focusedFields.has(fieldName);
    }

    public getEnumKeyByValue(enumValue: AgreementMetaDataConstants): string {
        return AgreementMetaDataUtils.getEnumKeyByValue(enumValue);
    }

    /**
     * Check if field has a PDF text reference
     */
    public hasTextReference(enumValue: AgreementMetaDataConstants): boolean {
        return this.highlightsService.hasEnumFieldTextReference(enumValue);
    }

    /**
     * Get the source text for a field to display in tooltip
     */
    public getSourceText(enumValue: AgreementMetaDataConstants): string {
        const textReference = this.highlightsService.getEnumFieldTextReference(enumValue);
        return textReference?.text || '';
    }
}
